import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client.js";
import { isCeremonyCancelled, passkeysSupported, registerPasskey } from "../../auth/passkeys.js";
import { sessionQuery, useSession } from "../../auth/session.js";
import { AccountExportCard } from "../../components/data-controls.js";
import { Button, Card, ErrorNotice, Field, Input } from "../../components/form.js";

export function SecurityPage() {
  const { t } = useTranslation();
  return (
    <section className="mx-auto flex max-w-3xl flex-col gap-6 p-6">
      <h1 className="text-[22px] text-ink">{t("settings.security.title")}</h1>
      <PasswordCard />
      <PasskeysCard />
      <TotpCard />
      <SessionsCard />
      <AccountExportCard />
    </section>
  );
}

function TotpCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const session = useSession();
  const enabled = session.data?.mfa.enabled ?? false;

  const [enrollment, setEnrollment] = useState<{
    secret: string;
    otpauthUri: string;
    qr: string;
  } | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [disableForm, setDisableForm] = useState({ password: "", code: "" });

  const enroll = useMutation({
    mutationFn: () => api.auth.mfa.enroll(),
    onSuccess: async (result) => {
      const qr = await QRCode.toDataURL(result.otpauthUri, { margin: 1, width: 192 });
      setEnrollment({ ...result, qr });
      setRecoveryCodes(null);
    },
  });
  const confirm = useMutation({
    mutationFn: () => api.auth.mfa.confirm(code),
    onSuccess: async (result) => {
      setRecoveryCodes(result.recoveryCodes);
      setEnrollment(null);
      setCode("");
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] });
    },
  });
  const disable = useMutation({
    mutationFn: () => api.auth.mfa.disable(disableForm),
    onSuccess: async () => {
      setDisableForm({ password: "", code: "" });
      setRecoveryCodes(null);
      await queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey });
    },
  });

  const onConfirm = (event: FormEvent) => {
    event.preventDefault();
    confirm.mutate();
  };
  const onDisable = (event: FormEvent) => {
    event.preventDefault();
    disable.mutate();
  };

  return (
    <Card title={t("settings.security.totp.title")}>
      <p className="text-sm text-ink-2">{t("settings.security.totp.description")}</p>

      {recoveryCodes ? (
        <div className="rounded-md border border-good bg-good-soft p-4">
          <p className="text-sm font-medium text-good">
            {t("settings.security.totp.recoveryTitle")}
          </p>
          <p className="mt-1 text-xs text-ink-2">{t("settings.security.totp.recoveryHint")}</p>
          <ul className="mt-3 grid grid-cols-2 gap-1 font-mono text-sm">
            {recoveryCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {enabled ? (
        <form onSubmit={onDisable} className="flex flex-col gap-3">
          <p className="text-sm font-medium text-good">{t("settings.security.totp.enabled")}</p>
          <Field label={t("auth.fields.password")}>
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={disableForm.password}
              onChange={(e) => setDisableForm((f) => ({ ...f, password: e.target.value }))}
            />
          </Field>
          <Field label={t("auth.mfa.code")}>
            <Input
              required
              inputMode="numeric"
              pattern="[0-9]{6}"
              autoComplete="one-time-code"
              className="max-w-40 font-mono tracking-widest"
              value={disableForm.code}
              onChange={(e) => setDisableForm((f) => ({ ...f, code: e.target.value }))}
            />
          </Field>
          <ErrorNotice error={disable.error} />
          <div>
            <Button type="submit" variant="danger" disabled={disable.isPending}>
              {t("settings.security.totp.disable")}
            </Button>
          </div>
        </form>
      ) : enrollment ? (
        <form onSubmit={onConfirm} className="flex flex-col gap-3">
          <div className="flex flex-col gap-4 sm:flex-row">
            <img
              src={enrollment.qr}
              alt={t("settings.security.totp.qrAlt")}
              className="h-48 w-48 rounded-md border border-rule bg-white"
            />
            <div className="text-sm text-ink-2">
              <p>{t("settings.security.totp.scan")}</p>
              <p className="mt-2 text-xs text-ink-3">{t("settings.security.totp.manual")}</p>
              <code className="mt-1 block break-all rounded bg-surface-2 px-2 py-1 font-mono text-xs text-ink">
                {enrollment.secret}
              </code>
            </div>
          </div>
          <Field label={t("settings.security.totp.firstCode")}>
            <Input
              required
              autoFocus
              inputMode="numeric"
              pattern="[0-9]{6}"
              autoComplete="one-time-code"
              className="max-w-40 font-mono tracking-widest"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <ErrorNotice error={confirm.error} />
          <div className="flex gap-2">
            <Button type="submit" disabled={confirm.isPending}>
              {t("settings.security.totp.confirm")}
            </Button>
            <Button variant="secondary" onClick={() => setEnrollment(null)}>
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="flex flex-col gap-3">
          <ErrorNotice error={enroll.error} />
          <div>
            <Button onClick={() => enroll.mutate()} disabled={enroll.isPending}>
              {t("settings.security.totp.enable")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function PasswordCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const mismatch = form.confirm.length > 0 && form.confirm !== form.next;
  const update = (key: keyof typeof form) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const change = useMutation({
    mutationFn: () => api.auth.password.change(form.current, form.next),
    onSuccess: async () => {
      setForm({ current: "", next: "", confirm: "" });
      await queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] });
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!mismatch) change.mutate();
  };

  return (
    <Card title={t("settings.security.password.title")}>
      <p className="text-sm text-ink-2">{t("settings.security.password.description")}</p>
      <form onSubmit={onSubmit} className="flex max-w-sm flex-col gap-3">
        <Field label={t("settings.security.password.current")}>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={form.current}
            onChange={(e) => update("current")(e.target.value)}
          />
        </Field>
        <Field label={t("settings.security.password.new")} hint={t("auth.fields.passwordHint")}>
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={form.next}
            onChange={(e) => update("next")(e.target.value)}
          />
        </Field>
        <Field
          label={t("settings.security.password.confirm")}
          {...(mismatch ? { hint: t("auth.reset.mismatch") } : {})}
        >
          <Input
            type="password"
            autoComplete="new-password"
            required
            value={form.confirm}
            onChange={(e) => update("confirm")(e.target.value)}
          />
        </Field>
        <ErrorNotice error={change.error} />
        {change.isSuccess ? (
          <p className="text-sm text-good">{t("settings.security.password.done")}</p>
        ) : null}
        <div>
          <Button type="submit" disabled={change.isPending || mismatch}>
            {t("settings.security.password.submit")}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PasskeysCard() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const supported = passkeysSupported();
  const passkeys = useQuery({
    queryKey: ["auth", "passkeys"],
    queryFn: () => api.auth.passkeys.list(),
  });
  const add = useMutation({
    mutationFn: () => registerPasskey(name.trim() || t("settings.security.passkeys.defaultName")),
    onSuccess: async () => {
      setName("");
      await queryClient.invalidateQueries({ queryKey: ["auth", "passkeys"] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.auth.passkeys.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth", "passkeys"] }),
  });
  const format = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(iso));

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate();
  };

  return (
    <Card title={t("settings.security.passkeys.title")}>
      <p className="text-sm text-ink-2">{t("settings.security.passkeys.description")}</p>
      {supported ? (
        <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <Field label={t("settings.security.passkeys.name")}>
              <Input
                maxLength={100}
                placeholder={t("settings.security.passkeys.namePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>
          </div>
          <Button type="submit" disabled={add.isPending}>
            {t("settings.security.passkeys.add")}
          </Button>
        </form>
      ) : (
        <p className="text-sm text-ink-3">{t("settings.security.passkeys.unsupported")}</p>
      )}
      {add.error && !isCeremonyCancelled(add.error) ? <ErrorNotice error={add.error} /> : null}
      <ErrorNotice error={passkeys.error ?? remove.error} />
      {passkeys.data && passkeys.data.passkeys.length === 0 ? (
        <p className="text-sm text-ink-3">{t("settings.security.passkeys.none")}</p>
      ) : null}
      <ul className="divide-y divide-rule">
        {(passkeys.data?.passkeys ?? []).map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-4 py-3 text-sm">
            <div className="min-w-0">
              <div className="truncate text-ink">
                {p.name}
                {p.backedUp ? (
                  <span className="ml-2 rounded bg-good-soft px-1.5 py-0.5 text-xs text-good">
                    {t("settings.security.passkeys.synced")}
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-ink-3">
                {t("settings.security.passkeys.added", { date: format(p.createdAt) })}
                {p.lastUsedAt
                  ? ` · ${t("settings.security.passkeys.lastUsed", { date: format(p.lastUsedAt) })}`
                  : ""}
              </div>
            </div>
            <Button
              variant="secondary"
              onClick={() => remove.mutate(p.id)}
              disabled={remove.isPending}
            >
              {t("settings.security.passkeys.remove")}
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SessionsCard() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: ["auth", "sessions"], queryFn: () => api.auth.sessions() });
  const revoke = useMutation({
    mutationFn: (id: string) => api.auth.revokeSession(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["auth", "sessions"] }),
  });
  const format = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(iso),
    );

  return (
    <Card title={t("settings.security.sessions.title")}>
      <ErrorNotice error={sessions.error ?? revoke.error} />
      <ul className="divide-y divide-rule">
        {(sessions.data?.sessions ?? []).map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-4 py-3">
            <div className="min-w-0 text-sm">
              <div className="truncate text-ink">
                {s.userAgent ?? t("settings.security.sessions.unknownClient")}
                {s.current ? (
                  <span className="ml-2 rounded bg-accent-soft px-1.5 py-0.5 text-xs text-accent">
                    {t("settings.security.sessions.current")}
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-ink-3">
                {s.ipAddress ?? "—"} · {t("settings.security.sessions.lastSeen")}{" "}
                {format(s.lastSeenAt)}
              </div>
            </div>
            {!s.current ? (
              <Button
                variant="secondary"
                onClick={() => revoke.mutate(s.id)}
                disabled={revoke.isPending}
              >
                {t("settings.security.sessions.revoke")}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}
